import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";

import { faBars } from "@fortawesome/pro-regular-svg-icons";

import { Avatar, ButtonIcon, Chip, Icon, Tooltip } from "@performyard/py-components";

import MobileDrawer from "./MobileDrawer";
import { SubNavbar } from "./SubNavbar";

import EmployeeSearch from "../../../containers/EmployeeSearch/EmployeeSearch";

import type { NavigationConfig } from "../../../api/hooks/defs/navigation";

import { getPromotionalUrl, shouldShowPromotionalAd } from "../mainNavConfig";
import type { MainNavItemConfig, NavItemConfig } from "../mainNavConfig";

const LEGACY_LOGO_FALLBACK_URL = "/static/img/py-logo/py-logo-and-text-medium-with-padding.png";
const LEGACY_MOBILE_LOGO_FALLBACK_URL = "/static/img/py-logo/py-logo-solo.png";

// NEW icon SVG component
const NewIconSvg = () => (
	<span className="new-text" style={{ marginLeft: "4px" }}>
		<svg
			width="32"
			height="16"
			viewBox="0 0 32 16"
			fill="currentColor"
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label="New feature"
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M4 0C1.79086 0 0 1.79086 0 4V12C0 14.2091 1.79086 16 4 16H28C30.2091 16 32 14.2091 32 12V4C32 1.79086 30.2091 0 28 0H4ZM27.1697 5.368L27.3617 9.46H28.2857L28.4777 5.368L28.5257 4H27.1337L27.1697 5.368ZM27.1937 11.92C27.3697 12.096 27.5817 12.184 27.8297 12.184C28.0857 12.184 28.2977 12.096 28.4657 11.92C28.6337 11.744 28.7177 11.52 28.7177 11.248C28.7177 10.976 28.6337 10.752 28.4657 10.576C28.2977 10.4 28.0857 10.312 27.8297 10.312C27.5817 10.312 27.3697 10.4 27.1937 10.576C27.0177 10.752 26.9297 10.976 26.9297 11.248C26.9297 11.52 27.0177 11.744 27.1937 11.92ZM3 4.192V12.04H4.332V8.608C4.332 8.168 4.308 7.72 4.26 7.264C4.22 6.808 4.184 6.372 4.152 5.956H4.2L5.004 7.624L7.452 12.04H8.88V4.192H7.548V7.6C7.548 8.04 7.568 8.496 7.608 8.968C7.656 9.44 7.696 9.876 7.728 10.276H7.68L6.876 8.608L4.428 4.192H3ZM10.875 4.192V12.04H15.723V10.864H12.267V8.548H15.087V7.372H12.267V5.368H15.603V4.192H10.875ZM18.112 12.04L16.552 4.192H17.98L18.664 8.152C18.728 8.568 18.792 8.988 18.856 9.412C18.928 9.828 18.992 10.244 19.048 10.66H19.096C19.184 10.244 19.268 9.828 19.348 9.412C19.436 8.988 19.524 8.568 19.612 8.152L20.572 4.192H21.784L22.744 8.152C22.832 8.56 22.92 8.976 23.008 9.4C23.096 9.816 23.184 10.236 23.272 10.66H23.32C23.384 10.236 23.448 9.816 23.512 9.4C23.576 8.984 23.636 8.568 23.692 8.152L24.376 4.192H25.708L24.208 12.04H22.48L21.508 7.912L21.316 7C21.26 6.696 21.208 6.396 21.16 6.1H21.112C21.056 6.396 20.996 6.696 20.932 7C20.876 7.304 20.816 7.608 20.752 7.912L19.804 12.04H18.112Z"
			/>
		</svg>
	</span>
);

export type NavbarProps = {
	// Navigation config and computed state
	config: NavigationConfig;
	mainNavItems: MainNavItemConfig[];
	activeMainTab: string | null;
	activeNavItem: NavItemConfig | null;
	parentNavItem: NavItemConfig | null;
	currentSubNavItems: NavItemConfig[];

	// Feature flags
	isDirectoryEnabled: boolean;
	isSurveysEnabled: boolean;
	isMeetingsEnabled: boolean;
	isEngagementEnabled: boolean;

	// User data
	userEmail?: string;

	// Optional overrides
	gitBranch?: string | null;
	headerElement?: HTMLElement | null;
	subNavElement?: HTMLElement | null;
};

/**
 * Pure presentational Navbar component.
 * Accepts all data as props - performs no data fetching.
 * Can be used in Storybook with mock data.
 */
export const Navbar = ({
	config,
	mainNavItems,
	activeMainTab,
	activeNavItem,
	parentNavItem,
	currentSubNavItems,
	isDirectoryEnabled,
	isSurveysEnabled,
	isMeetingsEnabled,
	isEngagementEnabled,
	userEmail = "",
	gitBranch = null,
	headerElement: headerElementProp = null,
	subNavElement: subNavElementProp = null,
}: NavbarProps) => {
	// Mobile drawer state
	const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

	// Avatar dropdown state
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// Portal targets - use props if provided, otherwise query DOM
	const [headerElement, setHeaderElement] = useState<HTMLElement | null>(
		headerElementProp || (() => document.getElementById("header"))
	);
	const [subNavElement, setSubNavElement] = useState<HTMLElement | null>(
		subNavElementProp || (() => document.getElementById("sub-nav-header"))
	);

	// Find portal elements if not provided as props
	useEffect(() => {
		if (!headerElementProp && !headerElement) {
			const element = document.getElementById("header");
			if (element) setHeaderElement(element);
		}
	}, [headerElementProp, headerElement]);

	useEffect(() => {
		if (!subNavElementProp && !subNavElement) {
			const element = document.getElementById("sub-nav-header");
			if (element) setSubNavElement(element);
		}
	}, [subNavElementProp, subNavElement]);

	// Set data attribute and background color on sub nav container based on active tab
	useEffect(() => {
		if (subNavElement && activeMainTab) {
			subNavElement.setAttribute("data-tab", activeMainTab);
			// Set background color - engagement tab is special
			if (activeMainTab === "engagement") {
				subNavElement.style.backgroundColor = "var(--py-primary-9)";
			} else {
				subNavElement.style.backgroundColor = "var(--py-neutral-2)";
			}
		} else if (subNavElement) {
			subNavElement.removeAttribute("data-tab");
			subNavElement.style.backgroundColor = "var(--py-neutral-2)";
		}
	}, [subNavElement, activeMainTab]);

	if (!headerElement) {
		return null;
	}

	const { isViewingSelf } = config;

	return (
		<>
			{/* Mobile Drawer */}
			<MobileDrawer isOpen={isMobileDrawerOpen} onClose={() => setIsMobileDrawerOpen(false)} />

			{/* Main Navbar - portals into #header */}
			{createPortal(
				<section id="react-navbar-content" role="banner" className="h-full">
					<div className="mx-auto flex h-full max-w-[1380px] items-center justify-between px-4 py-3">
						{/* Logo */}
						<a style={{ display: "block" }} href="/" aria-label="Go to home page">
							<img
								id="logo"
								src={config.logoUrl || LEGACY_LOGO_FALLBACK_URL}
								alt="Company Logo Dashboard"
							/>
							<img
								id="mobile-logo"
								src={config.mobileLogoUrl || LEGACY_MOBILE_LOGO_FALLBACK_URL}
								alt="Company Logo Dashboard"
							/>
						</a>

						{/* Git branch chip (dev environments only) */}
						{gitBranch && (
							<div className="ml-2 inline-block py-line-clamp-1">
								<Chip label={gitBranch} color="red" size={24} />
							</div>
						)}

						{/* Mobile nav button */}
						<div className="flex h-10 items-center justify-center md:hidden">
							<ButtonIcon
								size={32}
								icon={faBars}
								dangerouslySetClassName="text-[--company-icon-color] bg-none border-none"
								onClick={() => setIsMobileDrawerOpen(true)}
								aria-label="Open navigation menu"
								type="button"
							/>
						</div>

						{/* Main navigation links */}
						<nav
							aria-label="Primary navigation"
							id="react-navbar-links"
							className="hidden items-center gap-4 md:flex"
						>
							{/* Employee search - only render if directory is enabled */}
							{isDirectoryEnabled && <EmployeeSearch rootElementId={undefined} />}

							{mainNavItems.map((navItem) => {
								if (!navItem) return null;

								const isActive = activeMainTab === navItem.id;

								// Administration link should trigger full page load, not SPA navigation
								if (navItem.id === "administration") {
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<a
												href={navItem.href || "/"}
												className={`top-item ${isActive ? "opacity-100" : "opacity-50 transition-opacity duration-150 hover:opacity-100"}`}
												aria-label={navItem.title}
											>
												<Icon
													dangerouslySetClassName="text-[--company-icon-color]"
													icon={navItem.icon}
													size={20}
													aria-hidden="true"
												/>
											</a>
										</Tooltip>
									);
								}

								// Check if this is a promotional ad
								const isPromotionalAd = shouldShowPromotionalAd(navItem.id, {
									isAdmin: config.isAdmin,
									isBasicAdmin: config.isBasicAdmin,
									isSurveysEnabled,
									isMeetingsEnabled,
									isEngagementEnabled,
									currentPromotion: config.currentPromotion,
								});

								if (isPromotionalAd) {
									// Promotional ad with NEW icon - links to external page
									const promotionId = String(
										(config.currentPromotion as { _id?: unknown })?._id ?? ""
									);
									const promoUrl = getPromotionalUrl(navItem.id, userEmail, promotionId);
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<a
												href={promoUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="top-item text-[--company-icon-color] opacity-50 transition-opacity duration-150 hover:opacity-100"
												aria-label={`${navItem.title} (opens in new window)`}
												onClick={(e) => {
													e.preventDefault();
													window.open(promoUrl, `${navItem.id}-ad-page`);
												}}
											>
												<div className="flex items-center">
													<Icon
														dangerouslySetClassName="text-[--company-icon-color]"
														icon={navItem.icon}
														size={20}
														aria-hidden="true"
													/>
													<NewIconSvg />
												</div>
											</a>
										</Tooltip>
									);
								}

								// Special case for performance tab when viewing another user
								if (navItem.id === "performance" && !isViewingSelf) {
									return (
										<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
											<NavLink
												to={navItem.href || "/"}
												className="top-item item-text text-caption font-bold text-[--company-icon-color] opacity-50 transition-opacity duration-150 hover:opacity-100"
												aria-label="Return to my dashboard"
											>
												<i>My Dashboard</i>
											</NavLink>
										</Tooltip>
									);
								}

								// Regular nav link
								return (
									<Tooltip asChild key={navItem.id} content={navItem.title} placement="bottom">
										<NavLink
											to={navItem.href || "/"}
											className={`top-item ${isActive ? "opacity-100" : "opacity-50 transition-opacity duration-150 hover:opacity-100"}`}
											aria-label={navItem.title}
										>
											<Icon
												dangerouslySetClassName="text-[--company-icon-color]"
												icon={navItem.icon}
												size={20}
												aria-hidden="true"
											/>
										</NavLink>
									</Tooltip>
								);
							})}

							{/* User Avatar dropdown */}
							<div
								className="relative"
								onMouseEnter={() => setIsDropdownOpen(true)}
								onMouseLeave={() => setIsDropdownOpen(false)}
								onFocus={() => setIsDropdownOpen(true)}
								onBlur={() => setIsDropdownOpen(false)}
							>
								<button
									type="button"
									className="cursor-pointer border-none bg-transparent"
									aria-label="User account menu"
									aria-haspopup="true"
									aria-expanded={isDropdownOpen}
								>
									<Avatar
										size={28}
										id={config.currentUserId}
										alt={`${config.currentUserName} profile menu`}
										dangerouslySetClassName="border-neutral-100 border-solid border border-2"
									/>
								</button>

								{/* Dropdown menu */}
								<div
									className={`absolute right-0 top-full z-10 mt-2 w-56 origin-top-right rounded-lg border border-neutral-3 bg-white shadow-lg transition-all duration-150 ${isDropdownOpen ? "visible scale-100 opacity-100" : "invisible scale-95 opacity-0"} `}
									role="menu"
									aria-label="User menu"
								>
									{/* Caret */}
									<div className="absolute -top-2 right-3 h-4 w-4 rotate-45 transform border-l border-t border-neutral-3 bg-white" />

									<div className="relative rounded-lg bg-white py-2">
										{/* Return to original user if impersonating */}
										{config.possessor && (
											<a
												className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
												href="/unpossess/"
												role="menuitem"
												aria-label={`Stop impersonating and return to ${config.possessor.name}`}
												onClick={() => {
													if (typeof (window as any).posthogUtils !== "undefined") {
														(window as any).posthogUtils.resetForUserSwitch(true);
													} else if (typeof (window as any).posthog !== "undefined") {
														(window as any).posthog.reset(true);
													}
												}}
											>
												Return to {config.possessor.name}
											</a>
										)}

										{/* Super User link for overlords */}
										{config.isOverlord && (
											<a
												className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
												href="/super-user/"
												role="menuitem"
												aria-label="Go to super user administration"
											>
												Super User
											</a>
										)}

										<div
											id="react-task-watcher"
											className="watcher-in-dropdown react-task-watcher"
										/>

										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href={
												config.isEngagementOnly
													? "/settings/account/"
													: "/settings/notifications/"
											}
											role="menuitem"
											aria-label="Go to account settings"
										>
											Settings
										</a>
										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="#"
											role="menuitem"
											aria-label="Open Help Scout support beacon"
											onClick={(e) => {
												e.preventDefault();
												if (window.Beacon) {
													window.Beacon("open");
												}
											}}
										>
											Get Help
										</a>
										<a
											className="block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="http://support.performyard.com"
											target="_blank"
											rel="noopener noreferrer"
											role="menuitem"
											aria-label="Get support (opens in new window)"
										>
											Support
										</a>
										<a
											className="line-clamp-2 block px-4 py-2 text-paragraph-3 text-neutral-9 transition-colors hover:bg-neutral-1"
											href="/logout/"
											role="menuitem"
											aria-label={`Logout ${config.currentUserName}`}
										>
											Logout {config.currentUserName}
										</a>
									</div>
								</div>
							</div>
						</nav>
					</div>

					<div className="hidden md:block">
						{/* Sub Navbar - rendered below main nav */}
						<SubNavbar
							activeTabId={activeNavItem?.id || null}
							activeMainTab={activeMainTab}
							subNavItems={currentSubNavItems}
							ariaLabel={parentNavItem ? `${parentNavItem.title} navigation` : undefined}
						/>
					</div>
				</section>,
				headerElement
			)}
		</>
	);
};
